import { pick } from 'es-toolkit';

import { ProductionComponentInstance } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { InstancePositions } from '@paragon/production-app-utilities';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import {
  productionComponentInstancesDeleted,
  productionComponentInstancesLoaded,
} from '../store/productionComponentInstancesSlice';
import { AsyncThunkActionReturn, AsyncThunkActionVoid } from './thunkActions';

export function loadProductionComponentInstancesForProductionGroup(
  productionGroupGuid: string,
  spinner = true,
): AsyncThunkActionReturn<readonly ProductionComponentInstance[]> {
  return async function loadProductionComponentInstancesForProductionGroupThunkAction(dispatch, getState) {
    const spinnerId = spinner ? dispatch(addSpinnerMessage('Loading components for production group...')) : null;

    try {
      const state = getState();

      const productionComponentInstancesInState = Object.values(state.productionComponentInstances).filter(
        (productionComponentInstance) => productionComponentInstance.productionGroupGuid === productionGroupGuid,
      );
      if (productionComponentInstancesInState.length > 0) return productionComponentInstancesInState;

      const productionComponentInstances =
        await designService.productionComponentInstances.getForProductionGroup(productionGroupGuid);

      dispatch(productionComponentInstancesLoaded(productionComponentInstances));
      return productionComponentInstances;
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error loading components'));
      return [];
    } finally {
      if (spinnerId != null) dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function removeProductionComponentInstancesFromProductionGroup(
  productionComponentInstanceGuids: readonly string[],
  spinner = true,
): AsyncThunkActionVoid {
  return async function removeProductionComponentInstancesFromProductionGroupThunkAction(dispatch) {
    const spinnerId = spinner ? dispatch(addSpinnerMessage('Removing component from production group...')) : null;

    try {
      await designService.productionComponentInstances.bulkDelete(productionComponentInstanceGuids);

      dispatch(productionComponentInstancesDeleted(productionComponentInstanceGuids));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error removing component from production group'));
      throw exception;
    } finally {
      if (spinnerId != null) dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function removeProductionComponentInstancesFromBatch(
  productionComponentInstanceGuids: string[],
): AsyncThunkActionVoid {
  return async function removeProductionComponentInstancesFromBatchThunkAction(dispatch) {
    if (productionComponentInstanceGuids.length === 0) return;

    const spinnerId = dispatch(addSpinnerMessage('Removing components from batch...'));

    try {
      const updatedProductionComponentInstances = await designService.productionComponentInstances.patchRemoveFromBatch(
        productionComponentInstanceGuids,
      );
      dispatch(productionComponentInstancesLoaded(updatedProductionComponentInstances));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error removing component from batch'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function removeProductionComponentInstancesFromBundle(
  productionComponentInstanceGuids: readonly string[],
  userErrorReporting = true,
): AsyncThunkActionVoid {
  return async function removeProductionComponentInstancesFromBundleThunkAction(dispatch) {
    if (productionComponentInstanceGuids.length === 0) return;

    const spinnerId = dispatch(addSpinnerMessage('Removing components from bundle...'));

    try {
      const updatedProductionComponentInstances =
        await designService.productionComponentInstances.patchRemoveFromBundle(productionComponentInstanceGuids);
      dispatch(productionComponentInstancesLoaded(updatedProductionComponentInstances));
    } catch (exception) {
      console.error(exception);
      if (userErrorReporting) dispatch(enqueueError('Error removing component design from bundle'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function setInstanceBundlePositions(updatedPositions: InstancePositions): AsyncThunkActionVoid {
  return async function setInstanceBundlePositionThunkAction(dispatch, getState) {
    // Optimistically update the position to avoid the truss snapping back to where it was while the request is going
    // through
    const originalProductionComponentInstances = getState().productionComponentInstances;
    const optimisticallyUpdatedProductionComponentInstances: readonly ProductionComponentInstance[] = Object.entries(
      updatedPositions,
    ).map(([productionComponentInstanceGuid, updatedPosition]) => ({
      ...originalProductionComponentInstances[productionComponentInstanceGuid],
      bundlePositionX: updatedPosition.x,
      bundlePositionY: updatedPosition.y,
    }));
    dispatch(productionComponentInstancesLoaded(optimisticallyUpdatedProductionComponentInstances));

    try {
      await designService.productionComponentInstances.setBundlePositions(updatedPositions);
    } catch (exception) {
      console.error(exception);
      dispatch(
        productionComponentInstancesLoaded(
          Object.values(pick(originalProductionComponentInstances, Object.keys(updatedPositions))),
        ),
      );
      dispatch(enqueueError('Error setting instance position in bundle'));
    }
  };
}
